import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useAuthedApi } from '@/hooks/use-authed-api';
import { Sparkle } from '@/icons/Sparkle';
import { generateAPIUrl } from '@/utils/api-url';
import { Link, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import * as WebBrowser from 'expo-web-browser';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { ApiUserType } from '../../../api/user/[...everything]+api';

const userClient = hc<ApiUserType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const IMAGE = require('../../../../assets/images/onboard3.png');

async function loadSubscription(token: string, checkoutSessionId?: string) {
  const res = await userClient.api.user.me.subscription.$get(
    {
      query: { checkoutSessionId }
    },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  if (!res.ok) {
    throw new Error('Failed to load subscription');
  }

  const result = await res.json();

  if (result.valid !== true) {
    throw new Error('Failed to load subscription');
  }

  return result;
}

async function loadPortalUrl(token: string) {
  const res = await userClient.api.user.me.subscription.portal.$post(
    { json: { platform: Platform.OS === 'web' ? 'web' : 'app' } },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  if (!res.ok) {
    throw new Error('Failed to load portal URL');
  }

  const result = await res.json();

  if (result.valid !== true) {
    throw new Error('Failed to load portal URL');
  }

  return result.url;
}

export default function Subscription() {
  const { checkoutSessionId } = useLocalSearchParams() as {
    checkoutSessionId?: string;
  };
  const loadSubscriptionCallback = useCallback(
    (token: string) => loadSubscription(token, checkoutSessionId),
    [checkoutSessionId]
  );
  const { loading, data, authState } = useAuthedApi(loadSubscriptionCallback);

  const iapSource = data?.iapSource;
  const redirectToPortal = useCallback(async () => {
    if (!authState.token) {
      alert('Must be logged in to access the portal');
      return;
    }

    let url;
    if (iapSource === 'stripe') {
      url = await loadPortalUrl(authState.token);
    } else if (iapSource === 'ios') {
      url = 'https://apps.apple.com/account/subscriptions';
    } else if (iapSource === 'android') {
      url =
        'https://play.google.com/store/account/subscriptions?package=com.lexikon.lexikon';
    }

    if (url) {
      if (Platform.OS === 'web') {
        Linking.openURL(url);
      } else {
        WebBrowser.openBrowserAsync(url);
      }
    } else {
      alert(`Account Management unavailable for your account (${iapSource})`);
    }
  }, [authState.token, iapSource]);

  return (
    <Fragment>
      <Head>
        <title>Subscription - Lexikon AI</title>
      </Head>

      <SafeAreaView className="flex-1">
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          className="flex-1"
        >
          <View className="mt-4 flex-1 items-center justify-center">
            <Image source={IMAGE} style={styles.image} resizeMode="contain" />
            <View>
              {loading || !data ? (
                <View className="py-4">
                  <ActivityIndicator color="white" />
                </View>
              ) : data.subscription.planKeyname === 'free' ? (
                <View className="mt-4">
                  <Text className="text-center">
                    You are currently on the free plan.
                  </Text>
                  <View className="mx-12 mt-4">
                    <Link href="/upgrade">
                      <View className="flex flex-row items-center rounded-full bg-gray-800 p-4">
                        <Sparkle color="#fff" className="w-8" />
                        <Text className="native:font-bold pl-4">Upgrade</Text>
                      </View>
                    </Link>
                  </View>
                </View>
              ) : (
                <View className="mt-4">
                  <Text className="text-center leading-7">
                    You are currently on the{' '}
                    {data.subscription.planKeyname === 'premium'
                      ? 'Pro'
                      : data.subscription.planKeyname}{' '}
                    plan. Thanks for subscribing! 🎉
                  </Text>
                  <View className="mx-12 mt-4">
                    <Button onPress={redirectToPortal}>
                      <Text>Manage Subscription</Text>
                    </Button>
                  </View>
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Fragment>
  );
}

const styles = StyleSheet.create({
  image: {
    height: 200,
    width: 200
  }
});
